import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ArrowRight } from 'lucide-react';

const Products = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const productCategories = [
    {
      id: 1,
      title: 'Electronics & Technology',
      icon: '💻',
      shortDesc: 'Electronic components and IT infrastructure solutions.',
      products: [
        'Diodes',
        'Computers',
        'IT hardware',
        'Data center products',
        'Firewall systems',
        'Advanced screens and displays',
        'Network infrastructure equipment',
        'Electronic components',
        'Industrial electronics'
      ]
    },
    {
      id: 2,
      title: 'Agricultural Commodities',
      icon: '🌾',
      shortDesc: 'Premium agricultural products sourced globally.',
      products: [
        'Ethiopian premium coffee (G1–G5)',
        'Sesame seeds',
        'Moringa',
        'Red kidney beans',
        'Chickpeas',
        'Corn',
        'Sugar',
        'Black seed',
        'Other bulk commodities'
      ]
    },
    {
      id: 3,
      title: 'Agricultural Machinery & Irrigation Equipment',
      icon: '🚜',
      shortDesc: 'Agricultural machinery, irrigation, pumping and related equipment sourced for commercial farming and agricultural projects.',
      products: [
        'Agricultural machinery and equipment',
        'Submersible water pumps',
        'Agricultural pumping systems',
        'Solar-powered agricultural pumping systems',
        'MPPT solar pump controllers',
        'Irrigation systems and equipment',
        'HDPE irrigation pipes and fittings',
        'Drip-irrigation systems',
        'Agricultural machinery spare parts',
        'Mechanical and electrical components',
        'Agricultural project equipment and supplies'
      ]
    },
    {
      id: 4,
      title: 'Industrial Machinery',
      icon: '⚙️',
      shortDesc: 'Machinery and technical supply solutions.',
      products: [
        'Heavy machinery supply',
        'Manufacturing equipment',
        'Precision parts and components',
        'Generator systems',
        'Installation equipment',
        'Maintenance support products',
        'Industrial tools',
        'Machinery solutions'
      ]
    },
    {
      id: 5,
      title: 'Vehicles & Automotive',
      icon: '🚗',
      shortDesc: 'Vehicles and automotive supply solutions.',
      products: [
        'Heavy vehicles',
        'Commercial vehicles',
        'Passenger vehicles',
        'Car spare parts',
        'Engine components',
        'Fleet supply solutions',
        'Automotive parts and accessories'
      ]
    },
    {
      id: 6,
      title: 'Oil & Gas / Maintenance Supply',
      icon: '⚡',
      shortDesc: 'Industrial and energy sector support products.',
      products: [
        'Machine maintenance parts',
        'Custom high voltage blades',
        'Fan parts',
        'Motor parts',
        'Engine parts',
        'Industrial chemicals',
        'Oils and lubricants',
        'Mechanical maintenance equipment',
        'Oil and gas support products'
      ]
    },
    {
      id: 7,
      title: 'Construction Materials',
      icon: '🏗️',
      shortDesc: 'Construction and infrastructure materials.',
      products: [
        'Steel',
        'Cement',
        'Pipes and fittings',
        'Structural materials',
        'Construction supply items'
      ]
    },
    {
      id: 8,
      title: 'Bitumen & Asphalt',
      icon: '🛣️',
      shortDesc: 'Road construction materials.',
      products: [
        'Bitumen',
        'Asphalt',
        'Road construction materials'
      ]
    },
    {
      id: 9,
      title: 'Medical Equipment',
      icon: '🏥',
      shortDesc: 'Healthcare and medical supply.',
      products: [
        'Hospital equipment',
        'Medical devices',
        'Medical consumables'
      ]
    },
    {
      id: 10,
      title: 'Gold & Precious Metals',
      icon: '💰',
      shortDesc: 'Precious metals sourcing and supply.',
      products: [
        'Gold sourcing',
        'Precious metals',
        'Verified supplier network'
      ]
    }
  ];

  const toggleCategory = (categoryId) => {
    if (expandedCategory === categoryId) {
      setExpandedCategory(null);
    } else {
      setExpandedCategory(categoryId);
    }
  };

  return (
    <>
      <Helmet>
        <title>Industrial Products & Supply Solutions | Eaglesway Group</title>
        <meta 
          name="description" 
          content="Explore Eaglesway Group's premium product range: Electronics, Agricultural Commodities, Agricultural Machinery & Irrigation Equipment, Industrial Machinery, Vehicles, Oil & Gas, Construction Materials, and more. Reliable sourcing and global trade support." 
        />
        <meta name="keywords" content="electronics, agricultural commodities, agricultural machinery supplier UAE, irrigation equipment supplier UAE, agricultural water pumps, agricultural machinery spare parts, agricultural equipment sourcing, industrial machinery, vehicles, oil and gas, construction materials, bitumen, medical equipment, gold sourcing, precious metals" />
      </Helmet>
      
      <div className="min-h-screen bg-black text-white pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)', backgroundSize: '40px 40px'}}></div>
          </div>

          <div className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Our <span className="text-[#D4AF37]">Products</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Eaglesway Group supplies a wide range of products across multiple industries with reliable sourcing and global trade support.
            </p>
          </div>
        </section>

        {/* Interactive Product Categories - Accordion Style */}
        <section className="py-16 bg-black">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-4">
              {productCategories.map((category, index) => (
                <Card
                  key={category.id}
                  className={`bg-gradient-to-b from-gray-900 to-black border-2 border-gray-800 hover:border-[#D4AF37] transition-all duration-500 overflow-hidden ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                  style={{ transitionDelay: `${index * 50}ms` }}
                >
                  {/* Category Header - Clickable */}
                  <button
                    onClick={() => toggleCategory(category.id)}
                    className="w-full p-6 md:p-8 flex items-center justify-between text-left group hover:bg-gray-900/50 transition-colors duration-300"
                  >
                    <div className="flex items-center flex-1">
                      <div className="text-5xl mr-6">{category.icon}</div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-white group-hover:text-[#D4AF37] transition-colors mb-2">
                          {category.title}
                        </h3>
                        <p className="text-gray-400 text-sm md:text-base">
                          {category.shortDesc}
                        </p>
                      </div>
                    </div>

                    <div className="ml-4">
                      <ChevronDown 
                        className={`w-6 h-6 text-[#D4AF37] transition-transform duration-300 ${
                          expandedCategory === category.id ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </button>

                  {/* Expanded Content - Products List */}
                  <div
                    className={`overflow-hidden transition-all duration-500 ${
                      expandedCategory === category.id ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-6 md:px-8 pb-8 pt-4 border-t border-gray-800">
                      <div className="bg-black/50 rounded-xl p-6 mb-6">
                        <h4 className="text-lg font-bold text-[#D4AF37] mb-4">Available Products:</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {category.products.map((product, idx) => (
                            <div key={idx} className="flex items-start">
                              <div className="w-2 h-2 bg-[#D4AF37] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                              <span className="text-gray-300 text-sm">{product}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Request Quote Button */}
                      <Button
                        onClick={() => navigate('/quote')}
                        className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-black font-semibold py-6 text-lg transition-all duration-300 transform hover:scale-105 shadow-xl"
                      >
                        Request a Quote
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Sourcing Section */}
        <section className="py-20 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Custom <span className="text-[#D4AF37]">Sourcing</span> Solutions
            </h2>
            <p className="text-gray-400 text-lg mb-8 leading-relaxed">
              Looking for specific products not listed here? Our global sourcing network can help you find exactly what you need. We specialize in customized procurement solutions tailored to your business requirements.
            </p>
            <Button
              onClick={() => navigate('/contact')}
              className="bg-[#D4AF37] hover:bg-[#B8941F] text-black font-semibold px-10 py-6 text-lg transition-all duration-300 transform hover:scale-105"
            >
              Contact Us for Custom Sourcing
            </Button>
          </div>
        </section>

        {/* Industries We Serve */}
        <section className="py-20 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Industries We <span className="text-[#D4AF37]">Serve</span>
              </h2>
              <p className="text-gray-400 text-lg">
                Trusted by businesses across multiple sectors worldwide
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { name: 'Energy & Oil Gas', icon: '⚡' },
                { name: 'Technology & Electronics', icon: '💻' },
                { name: 'Food & Beverage', icon: '☕' },
                { name: 'Manufacturing', icon: '⚙️' },
                { name: 'Construction', icon: '🏗️' },
                { name: 'Healthcare', icon: '🏥' },
                { name: 'Agriculture', icon: '🌾' },
                { name: 'Precious Metals', icon: '💰' },
              ].map((industry, index) => (
                <Card
                  key={index}
                  className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 hover:border-[#D4AF37] p-6 text-center transition-all duration-300 transform hover:-translate-y-1"
                >
                  <div className="text-4xl mb-3">{industry.icon}</div>
                  <p className="text-white text-sm font-medium">{industry.name}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Products;